'use client';
import { useState } from 'react';
import { TypeCourseProduct } from '@/core/generated-types';

export interface TypeVideoPosition {
  partId: string;
  chapterId: string;
  subChapterId: string;
  muxVideoId: string;
}

export function useCourseStreamingQueue(
  courseProduct: TypeCourseProduct,
  initialVideoId?: string | undefined,
) {
  const [videoPositions, setVideoPositions] = useState(
    _generateStreamingQueue(courseProduct, initialVideoId),
  );

  const [activeVideoPosition, onDeckVideoPosition] = videoPositions;
  const activeSubChapter = _findSubChapter(activeVideoPosition, courseProduct);
  const onDeckSubChapter = _findSubChapter(onDeckVideoPosition, courseProduct);

  function regenerateStreamingQueueFromVideo(subChapterId: string) {
    setVideoPositions(_generateStreamingQueue(courseProduct, subChapterId));
  }

  function reportActiveVideoCompleted() {
    // when the course is completed, we'll leave the last video in the player
    const isLastVideo = videoPositions.length === 1;
    if (isLastVideo) {
      return;
    }

    const tempCoursePositionsQueue = [...videoPositions];
    // removes completed video from queue
    tempCoursePositionsQueue.shift();

    setVideoPositions(tempCoursePositionsQueue);
  }

  return {
    activeVideoPosition,
    onDeckVideoPosition,
    activeSubChapter,
    onDeckSubChapter,
    reportActiveVideoCompleted,
    regenerateStreamingQueueFromVideo,
  };
}

export function _findSubChapter(
  position: TypeVideoPosition,
  courseProduct: TypeCourseProduct,
) {
  if (!position) return null;

  const coursePart = courseProduct.fields.courseParts.find(
    (part) => part.sys.id === position.partId,
  );

  const courseChapter = coursePart?.fields?.chapters?.find(
    (chapter) => chapter.sys.id === position.chapterId,
  );

  return courseChapter?.fields?.subChapters?.find(
    (subChapter) => subChapter.sys.id === position.subChapterId,
  );
}

export function _generateStreamingQueue(
  course: TypeCourseProduct,
  initialMuxVideoId?: string | undefined,
) {
  const fullCourseQueue = course.fields.courseParts.flatMap((part) => {
    return part.fields.chapters.flatMap((chapter) => {
      return chapter.fields?.subChapters.map(
        (subChapter) =>
          ({
            partId: part.sys.id,
            chapterId: chapter.sys.id,
            subChapterId: subChapter.sys.id,
            muxVideoId: subChapter.fields.video.sys.id,
          }) as TypeVideoPosition,
      );
    });
  });

  if (initialMuxVideoId) {
    const positionIdx = fullCourseQueue.findIndex(
      (coursePosition) => coursePosition.muxVideoId === initialMuxVideoId,
    );

    if (positionIdx !== -1) {
      return fullCourseQueue.slice(positionIdx);
    }
  }

  return fullCourseQueue;
}

export default useCourseStreamingQueue;
